import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Input, TextareaAutosize, ToggleSwitch, Select, formatSelectOptions } from '../../../components'
import { useMessage, useModalDialog } from '../../../hooks'
import { useEventDialog } from '../hooks'
import { eventService } from '../services'
import { appName } from '../appConfig'
import '../index.css'

const EMPTY_DATA = {
  title:            '',
  description:      '',
  start:            '',
  end:              '',
  location:         '',
  url:              '',
  includeWeekends:  false,
  type:             'internal', // 'internal' | 'external'
  attendees:        [], // [{value: user_id, label: user_fullname}] — INTERNAL participants
  organizerUserId:  '', // optional; only the external guest-list/invite flow needs it
  inviteMessage:    '', // optional default invitation body, may contain {{merge_tags}}
}

// The vocabulary an organizer can use in the invite message. Must stay in sync with
// buildInvitationReplacements in api/apps/events/helpers/invitation.js — note the tag
// names deliberately differ from the column names ({{event_name}} reads event_title),
// because a tag name is user-facing text that can already sit in a saved message.
const MERGE_TAGS = [
  'guest_first_name',
  'guest_name',
  'guest_company',
  'guest_email',
  'event_name',
  'event_start_date',
  'event_location',
  'organizer_first_name',
]

function slugify(str) {
  return (str || '').toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

const URL_SCHEME_REGEX = /^https?:\/\//i

// <input type="datetime-local"> wants exactly 'YYYY-MM-DDTHH:MM', which is a prefix of the
// wall-clock text v_event emits — so this is a slice, not a Date round-trip. It has to be:
// this is the PUT-back path, and the value is sent verbatim if the user never touches the
// field. Re-parsing and re-formatting used to make that round-trip non-idempotent, so a 23:30
// event prefilled as 01:30 the next day and saving the form permanently moved it.
function toLocalInputValue(wallClock) {
  if (!wallClock) return ''
  return String(wallClock).slice(0, 16)
}

const EventEdit = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const isNew    = !slug

  const { showMessage } = useMessage()
  const { hideModalDialog } = useModalDialog()
  const { showDeleteEventDialog } = useEventDialog()

  const [formSlug, setFormSlug]   = useState('')
  const [data, setData]           = useState(EMPTY_DATA)
  const [users, setUsers]         = useState([])
  const [loading, setLoading]     = useState(!isNew)
  const [saving, setSaving]       = useState(false)
  const [invalidFields, setInvalidFields] = useState(new Set())

  useEffect(() => {
    const ctrl = new AbortController()
    eventService.getUsers(ctrl.signal)
      .then(res => setUsers(res.users || []))
      .catch(() => {}) // the picker is a convenience — fail silently, manual entry of the event itself still works
    return () => ctrl.abort()
  }, [])

  useEffect(() => {
    if (!isNew) {
      const ctrl = new AbortController()
      eventService.getBySlug(slug, ctrl.signal)
        .then(res => {
          const e = res.event
          setData({
            title:            e.event_title || '',
            description:      e.event_description || '',
            start:            toLocalInputValue(e.event_start_timestamp),
            end:              toLocalInputValue(e.event_end_timestamp),
            location:         e.event_location || '',
            url:              e.event_url || '',
            includeWeekends:  !!e.event_include_weekends,
            type:             e.event_type || 'internal',
            attendees:        (e.interested_users || []).map(u => ({ value: u.user_id, label: u.user_fullname })),
            organizerUserId:  e.user_id || '',
            inviteMessage:    e.event_invite_message || '',
          })
          setFormSlug(e.event_slug)
          setLoading(false)
        })
        .catch(err => { if (err.name !== 'AbortError') { showMessage('Kunde inte ladda', 'error'); setLoading(false) } })
      return () => ctrl.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isNew])

  // Auto-derive slug from title (only while creating, still editable)
  useEffect(() => {
    if (!isNew) return
    setFormSlug(slugify(data.title))
  }, [data.title, isNew])

  const clearInvalid = (key) => setInvalidFields(prev => { const n = new Set(prev); n.delete(key); return n })

  const set = (key, value) => {
    if (value) clearInvalid(key)
    setData(prev => ({ ...prev, [key]: value }))
  }

  const handleChange = (event) => {
    const { name, value } = event.target !== undefined ? event.target : event
    set(name, value)
  }

  // Select's isMulti onChange fires once per toggled option (not the whole
  // array) — toggle its presence in data.attendees ourselves, same pattern
  // as feedback/Analyze.js's handleInputChange for its isMulti filters.
  const handleAttendeesChange = ({ value, option }) => {
    setData(prev => {
      const alreadySelected = prev.attendees.some(a => a.value === value)
      const attendees = alreadySelected
        ? prev.attendees.filter(a => a.value !== value)
        : [...prev.attendees, { value, label: option?.label }]
      return { ...prev, attendees }
    })
  }

  const validate = () => {
    const errs = new Set()
    if (!data.title) errs.add('title')
    if (!data.start) errs.add('start')
    if (data.url && !URL_SCHEME_REGEX.test(data.url)) errs.add('url')
    return errs
  }

  const payload = () => ({
    slug:             formSlug,
    title:            data.title,
    description:      data.description,
    start:            data.start,
    end:              data.end || null,
    location:         data.location,
    url:              data.url,
    includeWeekends:  data.includeWeekends,
    type:             data.type,
    // Sent as '' → null by the API when cleared, so an event can lose its organizer.
    organizerUserId:  data.organizerUserId || null,
    inviteMessage:    data.inviteMessage,
  })

  // Separate from the event save itself — a failure here shouldn't read as
  // "the whole save failed" when the event row actually saved fine, and
  // surfacing its own specific error makes a real problem here (as opposed
  // to the event fields) obvious instead of being masked by a generic
  // "Kunde inte spara".
  const saveAttendees = async (eventSlug) => {
    try {
      await eventService.setAttendees(eventSlug, data.attendees.map(a => a.value))
      return true
    } catch (e) {
      showMessage(e.message || 'Eventet sparades, men deltagarna kunde inte uppdateras', 'error')
      return false
    }
  }

  const handleSave = async () => {
    const errs = validate()
    if (errs.size > 0) {
      setInvalidFields(errs)
      showMessage('Kontrollera fälten nedan', 'error')
      return
    }
    setSaving(true)
    try {
      if (isNew) {
        const res = await eventService.create(payload())
        const attendeesOk = await saveAttendees(res.event.event_slug)
        if (attendeesOk) showMessage('Skapat!')
        navigate(`/${appName}/list`)
      } else {
        await eventService.update(slug, payload())
        const attendeesOk = await saveAttendees(formSlug)
        if (attendeesOk) showMessage('Sparat!')
        navigate(`/${appName}/list`)
      }
    } catch (e) {
      showMessage(e.message || 'Kunde inte spara', 'error')
    }
    setSaving(false)
  }

  const deleteEvent = () => {
    showDeleteEventDialog({ title: data.title, slug: formSlug }, async () => {
      await eventService.remove(slug)
        .then(() => { hideModalDialog(); showMessage('Borttaget'); navigate(`/${appName}/list`) })
        .catch(err => showMessage(err.message || 'Kunde inte ta bort', 'error'))
    })
  }

  if (loading) return <div className="ev-page"><p style={{ color: 'var(--color-dark-grey)' }}>Laddar…</p></div>

  const userOptions = formatSelectOptions({ options: users, optionValue: 'user_id', optionLabel: 'name' })

  return (
    <div className="ev-page">
      <div className="ev-page-header">
        <h1>{isNew ? 'Nytt event' : data.title}</h1>
      </div>

      <div className="ev-form-card">
        <div className="form-block vertical">
          <Input type="text" name="title"
            label={<>Titel<span className="ev-required">*</span></>}
            value={data.title} onChange={handleChange} placeholder="t.ex. Data Innovation Summit"
            validation={invalidFields.has('title') ? { valid: false, message: 'Titel är obligatoriskt' } : undefined} />
        </div>

        <div className="form-block vertical">
          <div className="ev-type-switch" role="group" aria-label="Internt eller externt event">
            <div className={"ev-type-switch-thumb" + (data.type === 'external' ? ' right' : '')} />
            <button type="button" className={"ev-type-switch-option" + (data.type !== 'external' ? ' active' : '')} onClick={() => set('type', 'internal')}>Internt</button>
            <button type="button" className={"ev-type-switch-option" + (data.type === 'external' ? ' active' : '')} onClick={() => set('type', 'external')}>Externt</button>
          </div>
        </div>

        <div className="ev-row-tight">
          <div className="form-block vertical">
            <label>Start<span className="ev-required">*</span></label>
            <input className="ev-plain-input ev-datetime-input" type="datetime-local" name="start" value={data.start} onChange={handleChange} />
            {invalidFields.has('start') && <p className="ev-error-text">Starttid är obligatoriskt</p>}
          </div>
          <div className="form-block vertical">
            <label>Slut<span className="ev-optional">(valfritt)</span></label>
            <input className="ev-plain-input ev-datetime-input" type="datetime-local" name="end" value={data.end} onChange={handleChange} />
          </div>
        </div>

        <div className="form-block vertical">
          <ToggleSwitch
            label="Inkludera helger"
            value={data.includeWeekends}
            onChange={e => set('includeWeekends', e.target.checked)}
          />
        </div>

        <div className="form-block vertical">
          <label>Deltagare<span className="ev-optional">(valfritt)</span></label>
          <Select
            name="attendees"
            value={data.attendees}
            options={userOptions}
            onChange={handleAttendeesChange}
            isMulti
            isClearable
            showMultiLabel
            placeholder="Sök och lägg till deltagare..."
          />
        </div>

        <div className="form-block vertical">
          <Input type="text" name="location" label={<>Plats<span className="ev-optional">(valfritt)</span></>} value={data.location} onChange={handleChange} placeholder="t.ex. WeWork, Stockholm" />
        </div>

        <div className="form-block vertical">
          <Input type="text" name="url" label={<>Länk<span className="ev-optional">(valfritt)</span></>} value={data.url} onChange={handleChange} placeholder="https://..."
            validation={invalidFields.has('url') ? { valid: false, message: 'Länken måste börja med http:// eller https://' } : undefined} />
        </div>

        <div className="form-block vertical">
          <label>Beskrivning<span className="ev-optional">(valfritt)</span></label>
          <TextareaAutosize name="description" value={data.description} onChange={handleChange} minRows={4} />
        </div>

        {/* --- External guest list: only needed for events we invite outsiders to. Both
            fields are optional, so a calendar-only event never has to touch them. --- */}
        <div className="form-block vertical">
          <label>Arrangör<span className="ev-optional">(valfritt — krävs för att skicka inbjudan)</span></label>
          <Select
            name="organizerUserId"
            value={data.organizerUserId}
            options={userOptions}
            onChange={handleChange}
            isNullable
            isClearable
            placeholder="Välj arrangör..."
          />
        </div>

        <div className="form-block vertical">
          <label>Inbjudningstext<span className="ev-optional">(valfritt)</span></label>
          <TextareaAutosize name="inviteMessage" value={data.inviteMessage} onChange={handleChange} minRows={4}
            placeholder="Skrivs in i mitten av inbjudningsmejlet — hälsning och avsändare läggs till automatiskt." />
          <div className="ev-mergetag-row">
            <span className="ev-mergetag-label">Infoga tagg:</span>
            {MERGE_TAGS.map(tag => (
              <button
                type="button"
                key={tag}
                className="ev-mergetag"
                onClick={() => set('inviteMessage', `${data.inviteMessage || ''}{{${tag}}}`)}
              >{`{{${tag}}}`}</button>
            ))}
          </div>
          <p className="ev-mergetag-hint">
            Taggar byts ut per gäst. En okänd tagg lämnas som den är, så stavfel syns i förhandsgranskningen.
          </p>
        </div>

        <div className="ev-form-actions">
          {!isNew && <Button value="Ta bort" className="button ev-danger-button" onClick={deleteEvent} />}
          <Button value="Spara" className="button main-button" onClick={handleSave} loading={saving} />
        </div>
      </div>
    </div>
  )
}

export default EventEdit
